// Axios instance for admin API. Automatically:
//   - attaches Authorization: Bearer <token>
//   - on 401, clears token + redirects to /admin/login?next=<currentPath>
import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

export const ADMIN_TOKEN_KEY = "admin_token";
export const ADMIN_EXP_KEY = "admin_token_exp";

export function getStoredToken() {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function storeToken(token, expiresAt) {
  try {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
    if (expiresAt) localStorage.setItem(ADMIN_EXP_KEY, expiresAt);
  } catch {}
}

export function clearStoredToken() {
  try {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_EXP_KEY);
  } catch {}
}

export function isTokenExpired() {
  try {
    const exp = localStorage.getItem(ADMIN_EXP_KEY);
    if (!exp) return false;
    return new Date(exp).getTime() <= Date.now();
  } catch {
    return false;
  }
}

const adminApi = axios.create({ baseURL: BASE });

adminApi.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearStoredToken();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin/login")) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/admin/login?next=${next}`);
      }
    }
    return Promise.reject(err);
  }
);

// Raw fetch helper for the CSV download (need full response object).
export async function downloadCsv(params) {
  const token = getStoredToken();
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/leads/export.csv?${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    if (res.status === 401) {
      clearStoredToken();
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.replace(`/admin/login?next=${next}`);
    }
    throw new Error(`CSV export failed (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] || "leads_export.csv";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default adminApi;
